import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useTheme, type ThemeColors } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Z_INDEX, zStyle } from '../../lib/zLayers';
import type { MenuItem } from './Sidebar';
import { buildTopMenuRoots } from './config/SidebarItems';

export type TopMenuEntry = {
  label: string;
  path: string;
  icon?: React.ReactNode;
  /** Column / primary group (e.g. Finance, Human Resources). */
  groupKey: string;
  /** Icon for the column header (module icon from sidebar). */
  groupIcon?: React.ReactNode;
  /** Optional nested sub-group within the column (e.g. Crew, Supplies). */
  subGroupKey?: string | null;
  /** Icon for the sub-group header (folder icon from sidebar). */
  subGroupIcon?: React.ReactNode;
};

type TopMenuNavProps = {
  items: MenuItem[];
  bookmarkEntries: TopMenuEntry[];
};

const HOVER_OPEN_DELAY_MS = 100;
const CLOSE_DELAY_MS = 280;

function isEntryPathActive(pathname: string, entryPath: string) {
  return pathname === entryPath || (entryPath !== '/' && pathname.startsWith(`${entryPath}/`));
}

function isFolderItem(item: MenuItem): boolean {
  return Array.isArray(item.children) && item.children.length > 0 && !item.path && !item.views?.length;
}

/** Same icon the sidebar shows for a row: item icon first, then view icon. */
function sidebarItemIcon(item: MenuItem): React.ReactNode | undefined {
  if (item.icon) return item.icon;
  const viewIcon = item.views?.find((v) => v.icon)?.icon;
  return viewIcon;
}

/** Collect leaf links from an item tree under a fixed group / optional sub-group. */
export function collectMenuEntries(
  item: MenuItem,
  groupKey: string,
  options?: {
    subGroupKey?: string | null;
    groupIcon?: React.ReactNode;
    subGroupIcon?: React.ReactNode;
  },
): TopMenuEntry[] {
  const entries: TopMenuEntry[] = [];
  const subGroupKey = options?.subGroupKey ?? null;
  const groupIcon = options?.groupIcon;
  const subGroupIcon = options?.subGroupIcon;
  const icon = sidebarItemIcon(item);

  if (Array.isArray(item.views) && item.views.length > 0) {
    for (const view of item.views) {
      if (view.path) {
        const label =
          item.views.length > 1
            ? `${item.name} – ${(view as { label?: string }).label ?? view.path}`
            : item.name;
        entries.push({
          label: label || view.path,
          path: view.path,
          icon: icon ?? view.icon,
          groupKey,
          groupIcon,
          subGroupKey,
          subGroupIcon,
        });
      }
    }
  } else if (item.path) {
    entries.push({
      label: item.name ?? item.path,
      path: item.path,
      icon,
      groupKey,
      groupIcon,
      subGroupKey,
      subGroupIcon,
    });
  }

  if (Array.isArray(item.children)) {
    for (const child of item.children) {
      entries.push(
        ...collectMenuEntries(child, groupKey, {
          subGroupKey,
          groupIcon,
          subGroupIcon,
        }),
      );
    }
  }

  return entries;
}

/**
 * Build entries for one module column. Direct leaf children sit ungrouped;
 * nested folders become sub-groups (Crew, Supplies, …).
 */
function collectModuleColumnEntries(module: MenuItem): TopMenuEntry[] {
  const groupKey = module.name ?? '';
  const groupIcon = sidebarItemIcon(module);
  const entries: TopMenuEntry[] = [];
  const base = { groupIcon };

  // Rare: module itself is a leaf
  if (module.path || module.views?.length) {
    entries.push(
      ...collectMenuEntries({ ...module, children: undefined }, groupKey, {
        ...base,
        subGroupKey: null,
      }),
    );
  }

  for (const child of module.children || []) {
    if (isFolderItem(child)) {
      entries.push(
        ...collectMenuEntries(child, groupKey, {
          ...base,
          subGroupKey: child.name,
          subGroupIcon: sidebarItemIcon(child),
        }),
      );
    } else if (child.children?.length) {
      entries.push(
        ...collectMenuEntries({ ...child, children: undefined }, groupKey, {
          ...base,
          subGroupKey: null,
        }),
      );
      entries.push(
        ...collectMenuEntries({ ...child, path: undefined, views: undefined }, groupKey, {
          ...base,
          subGroupKey: child.name,
          subGroupIcon: sidebarItemIcon(child),
        }),
      );
    } else {
      entries.push(
        ...collectMenuEntries(child, groupKey, {
          ...base,
          subGroupKey: null,
        }),
      );
    }
  }

  return entries;
}

/** Direct children of a top-level nav group become mega-menu columns. */
export function collectTopGroupEntries(item: MenuItem): TopMenuEntry[] {
  if (Array.isArray(item.children) && item.children.length > 0 && isFolderItem(item)) {
    const entries: TopMenuEntry[] = [];
    for (const child of item.children) {
      entries.push(...collectModuleColumnEntries(child));
    }
    return entries;
  }
  return collectModuleColumnEntries(item);
}

type MenuSubGroup = {
  key: string | null;
  icon?: React.ReactNode;
  items: TopMenuEntry[];
};
type MenuSection = {
  key: string;
  icon?: React.ReactNode;
  subgroups: MenuSubGroup[];
  items: TopMenuEntry[];
};

function buildMenuSections(entries: TopMenuEntry[]): MenuSection[] {
  const order: string[] = [];
  const map = new Map<string, TopMenuEntry[]>();
  for (const entry of entries) {
    if (!map.has(entry.groupKey)) {
      map.set(entry.groupKey, []);
      order.push(entry.groupKey);
    }
    map.get(entry.groupKey)!.push(entry);
  }

  return order.map((key) => {
    const items = map.get(key)!;
    const groupIcon = items.find((e) => e.groupIcon)?.groupIcon;
    const subOrder: Array<string | null> = [];
    const subMap = new Map<string | null, TopMenuEntry[]>();
    for (const entry of items) {
      const sk = entry.subGroupKey?.trim() ? entry.subGroupKey : null;
      if (!subMap.has(sk)) {
        subMap.set(sk, []);
        subOrder.push(sk);
      }
      subMap.get(sk)!.push(entry);
    }
    subOrder.sort((a, b) => {
      if (a === null && b !== null) return -1;
      if (a !== null && b === null) return 1;
      return 0;
    });
    const subgroups = subOrder.map((sk) => {
      const subItems = subMap.get(sk)!;
      return {
        key: sk,
        icon: subItems.find((e) => e.subGroupIcon)?.subGroupIcon,
        items: subItems,
      };
    });
    return { key, icon: groupIcon, subgroups, items };
  });
}

const TopMenuNav: React.FC<TopMenuNavProps> = ({ items, bookmarkEntries }) => {
  const location = useLocation();
  const { colors } = useTheme();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [panelBox, setPanelBox] = useState({ top: 0, left: 0, width: 0 });

  const navRef = useRef<HTMLElement | null>(null);
  const openIndexRef = useRef<number | null>(null);
  const openDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mouseInNavRef = useRef(false);
  const mouseInPanelRef = useRef(false);
  /** When set, menu stays open until outside click / link / Escape (click-to-pin). */
  const pinnedRef = useRef(false);

  openIndexRef.current = openIndex;

  const clearOpenDelay = () => {
    if (openDelayRef.current) {
      clearTimeout(openDelayRef.current);
      openDelayRef.current = null;
    }
  };
  const clearCloseDelay = () => {
    if (closeDelayRef.current) {
      clearTimeout(closeDelayRef.current);
      closeDelayRef.current = null;
    }
  };

  const measurePanelBox = () => {
    const el = navRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPanelBox({
      top: rect.bottom,
      left: rect.left,
      width: rect.width,
    });
  };

  const closeMenu = () => {
    clearOpenDelay();
    clearCloseDelay();
    pinnedRef.current = false;
    mouseInPanelRef.current = false;
    setOpenIndex(null);
  };

  const scheduleClose = () => {
    clearOpenDelay();
    if (pinnedRef.current) return;
    if (mouseInNavRef.current || mouseInPanelRef.current) return;
    clearCloseDelay();
    closeDelayRef.current = setTimeout(() => {
      closeDelayRef.current = null;
      if (pinnedRef.current || mouseInNavRef.current || mouseInPanelRef.current) return;
      setOpenIndex(null);
    }, CLOSE_DELAY_MS);
  };

  const scheduleOpen = (index: number) => {
    clearCloseDelay();
    clearOpenDelay();
    openDelayRef.current = setTimeout(() => {
      openDelayRef.current = null;
      // Hover-open is not pinned
      if (!pinnedRef.current || openIndexRef.current === index) {
        pinnedRef.current = false;
      }
      measurePanelBox();
      setOpenIndex(index);
    }, HOVER_OPEN_DELAY_MS);
  };

  const openImmediately = (index: number) => {
    clearOpenDelay();
    clearCloseDelay();
    measurePanelBox();
    setOpenIndex((prev) => {
      if (prev === index && pinnedRef.current) {
        pinnedRef.current = false;
        return null;
      }
      pinnedRef.current = true;
      return index;
    });
  };

  useEffect(() => {
    return () => {
      clearOpenDelay();
      clearCloseDelay();
    };
  }, []);

  useEffect(() => {
    if (openIndex === null) return;
    measurePanelBox();
    const onReposition = () => measurePanelBox();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [openIndex]);

  const groups = useMemo(() => {
    const roots = buildTopMenuRoots(items);
    return roots
      .map((item, index) => ({
        item,
        index,
        entries: collectTopGroupEntries(item),
        label: item.name || `Group ${index + 1}`,
      }))
      .filter((g) => g.entries.length > 0);
  }, [items]);

  let openEntries: TopMenuEntry[] = [];
  let openTitle = '';
  if (openIndex === -1) {
    openEntries = bookmarkEntries;
    openTitle = 'Bookmarks';
  } else if (openIndex != null) {
    const g = groups.find((x) => x.index === openIndex);
    openEntries = g?.entries ?? [];
    openTitle = g?.label ?? '';
  }

  const sections = buildMenuSections(openEntries);
  const isOpen = openIndex !== null && openEntries.length > 0;

  return (
    <>
      <nav
        ref={navRef}
        className={cn(
          'relative flex items-center border-b',
          colors.sidebarBackground,
          colors.sidebarText,
          colors.sidebarBorder,
          isOpen && 'z-[60]',
        )}
        style={isOpen ? zStyle(Z_INDEX.topMenuDropdown + 1) : undefined}
        aria-label="Main navigation"
        onMouseEnter={() => {
          mouseInNavRef.current = true;
          clearCloseDelay();
        }}
        onMouseLeave={() => {
          mouseInNavRef.current = false;
          scheduleClose();
        }}
      >
        <div className="flex min-h-[40px] flex-1 items-stretch gap-0 overflow-x-auto px-1 sm:px-2 scrollbar-thin">
          {bookmarkEntries.length > 0 && (
            <NavTrigger
              colors={colors}
              label="Bookmarks"
              active={bookmarkEntries.some((e) => isEntryPathActive(location.pathname, e.path))}
              open={openIndex === -1}
              onMouseEnter={() => scheduleOpen(-1)}
              onClick={() => openImmediately(-1)}
              leading={
                <svg className="h-3.5 w-3.5 text-amber-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.966a1 1 0 00.95.69h4.173c.969 0 1.371 1.24.588 1.81l-3.377 2.454a1 1 0 00-.364 1.118l1.287 3.966c.3.922-.755 1.688-1.538 1.118l-3.378-2.454a1 1 0 00-1.176 0l-3.378 2.454c-.783.57-1.838-.196-1.538-1.118l1.287-3.966a1 1 0 00-.364-1.118L2.002 9.393c-.783-.57-.38-1.81.588-1.81h4.173a1 1 0 00.95-.69l1.286-3.966z" />
                </svg>
              }
            />
          )}

          {groups.map(({ index, entries, label }) => {
            const active = entries.some((entry) => isEntryPathActive(location.pathname, entry.path));
            return (
              <NavTrigger
                key={`${label}-${index}`}
                colors={colors}
                label={label}
                active={active}
                open={openIndex === index}
                onMouseEnter={() => scheduleOpen(index)}
                onClick={() => openImmediately(index)}
              />
            );
          })}
        </div>
      </nav>

      {isOpen &&
        createPortal(
          <>
            {/* Backdrop starts below the nav so it never steals hover from triggers */}
            <div
              className={cn('fixed bottom-0', colors.background, 'opacity-60')}
              style={{
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                ...zStyle(Z_INDEX.topMenuBackdrop),
              }}
              aria-hidden
              onClick={closeMenu}
            />
            <div
              role="menu"
              className={cn(
                'fixed flex flex-col border-b shadow-xl',
                colors.surface,
                colors.border,
                'animate-in fade-in slide-in-from-top-1 duration-150',
              )}
              style={{
                top: panelBox.top,
                left: panelBox.left,
                width: panelBox.width,
                maxHeight: `calc(100vh - ${panelBox.top}px)`,
                ...zStyle(Z_INDEX.topMenuDropdown),
              }}
              onMouseEnter={() => {
                mouseInPanelRef.current = true;
                clearCloseDelay();
              }}
              onMouseLeave={() => {
                mouseInPanelRef.current = false;
                scheduleClose();
              }}
            >
              <div
                className={cn(
                  'flex shrink-0 items-center justify-between gap-3 border-b px-4 py-2.5 sm:px-6 lg:px-8',
                  colors.border,
                  colors.headerBg,
                )}
              >
                <div className="min-w-0">
                  {!(
                    openEntries.length === 1 &&
                    openEntries[0].label.trim().toLowerCase() === openTitle.trim().toLowerCase()
                  ) && (
                    <>
                      <h2 className={cn('text-sm font-semibold tracking-tight', colors.text)}>
                        {openTitle}
                      </h2>
                      <p className={cn('text-[11px]', colors.textMuted)}>
                        {openEntries.length} {openEntries.length === 1 ? 'page' : 'pages'}
                      </p>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={closeMenu}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
                    colors.border,
                    colors.textSecondary,
                    colors.interactiveHover,
                  )}
                  aria-label="Close menu"
                >
                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>

              <div className="min-h-0 flex-1 overflow-auto overscroll-contain scrollbar-thin">
                <div className="px-4 py-3 sm:px-6 lg:px-8">
                  <div
                    className={cn(
                      'grid gap-x-8 gap-y-4',
                      sections.length === 1
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                        : sections.length === 2
                          ? 'min-w-[36rem] grid-cols-2'
                          : sections.length === 3
                            ? 'min-w-[54rem] grid-cols-3'
                            : 'min-w-[72rem] grid-cols-2 sm:grid-cols-3 xl:grid-cols-4',
                    )}
                  >
                    {sections.map((section) => {
                      const onlyItem =
                        section.items.length === 1 && section.subgroups.length === 1
                          ? section.items[0]
                          : null;
                      const headerMatchesOnlyItem =
                        !!onlyItem &&
                        onlyItem.label.trim().toLowerCase() === section.key.trim().toLowerCase();
                      const showSectionHeader = sections.length > 1 && !headerMatchesOnlyItem;
                      const hasNamedSubgroups = section.subgroups.some((s) => s.key != null);

                      return (
                        <div
                          key={section.key}
                          className={cn(
                            'min-w-[12rem]',
                            hasNamedSubgroups && 'min-w-[22rem] sm:col-span-2 xl:col-span-2',
                          )}
                        >
                          {showSectionHeader && (
                            <p
                              className={cn(
                                'mb-1.5 flex items-center gap-2 border-b pb-1 text-[11px] font-semibold tracking-tight',
                                colors.border,
                                colors.text,
                              )}
                            >
                              {section.icon && (
                                <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
                                  {section.icon}
                                </span>
                              )}
                              <span>{section.key}</span>
                            </p>
                          )}
                          <div
                            className={cn(
                              hasNamedSubgroups
                                ? 'flex flex-wrap items-start gap-x-5 gap-y-3'
                                : 'space-y-0',
                            )}
                          >
                            {section.subgroups.map((sub) => (
                              <div
                                key={`${section.key}::${sub.key ?? '_'}`}
                                className={cn(hasNamedSubgroups ? 'min-w-[10.5rem] max-w-[15rem] flex-1' : undefined)}
                              >
                                {sub.key != null && (
                                  <p
                                    className={cn(
                                      'mb-0.5 flex items-center gap-1.5 px-1.5 text-[10px] font-semibold uppercase tracking-[0.14em]',
                                      colors.textMuted,
                                    )}
                                  >
                                    {sub.icon && (
                                      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center [&>svg]:h-3.5 [&>svg]:w-3.5">
                                        {sub.icon}
                                      </span>
                                    )}
                                    <span>{sub.key}</span>
                                  </p>
                                )}
                                <ul className="space-y-0">
                                  {sub.items.map((entry) => {
                                    const active = isEntryPathActive(location.pathname, entry.path);
                                    return (
                                      <li key={`${entry.path}-${entry.label}-${sub.key ?? ''}`}>
                                        <Link
                                          to={entry.path}
                                          role="menuitem"
                                          onClick={closeMenu}
                                          className={cn(
                                            'group flex items-center gap-2 rounded-md px-1.5 py-1 text-sm transition-colors',
                                            active
                                              ? cn(colors.surfaceHover, colors.primaryFg, 'font-semibold')
                                              : cn(colors.textSecondary, colors.interactiveHover),
                                          )}
                                        >
                                          <span
                                            className={cn(
                                              'flex h-6 w-6 shrink-0 items-center justify-center rounded [&>svg]:h-4 [&>svg]:w-4',
                                              active
                                                ? cn(colors.primary, colors.primaryText)
                                                : cn(colors.surfaceHover, colors.textMuted),
                                            )}
                                          >
                                            {entry.icon || (
                                              <span className="h-1 w-1 rounded-full bg-current opacity-60" />
                                            )}
                                          </span>
                                          <span className="truncate leading-snug">{entry.label}</span>
                                        </Link>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

function NavTrigger({
  colors,
  label,
  active,
  open,
  onMouseEnter,
  onClick,
  leading,
}: {
  colors: ThemeColors;
  label: string;
  active: boolean;
  open: boolean;
  onMouseEnter: () => void;
  onClick: () => void;
  leading?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(
        'relative flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium tracking-wide transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
        open
          ? cn(colors.sidebarActive, colors.sidebarActiveText)
          : active
            ? colors.sidebarTextHover
            : cn(colors.sidebarText, 'opacity-75', colors.sidebarItemHover),
      )}
    >
      {leading}
      <span>{label}</span>
      <svg
        className={cn('h-2.5 w-2.5 opacity-50 transition-transform', open && 'rotate-180 opacity-90')}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
      </svg>
      {(open || active) && (
        <span className={cn('absolute inset-x-2 bottom-0 h-[2px] rounded-full', colors.accent)} aria-hidden />
      )}
    </button>
  );
}

export default TopMenuNav;
